<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi, ApiError } from 'soykajs/composables/api'
import { useMessageStack } from '@/stores/messagestack'
import OtpCodeInput from '@/components/OtpCodeInput.vue'

const props = defineProps({
  username: { type: String, required: true },
  password: { type: String, required: true },
  service: { type: String, required: true },
})

const emit = defineEmits(['success', 'close'])

const { t } = useI18n()
const { push: openModal } = useMessageStack()
const code = ref('')
const loading = ref(false)
const verifyModel = ref(null)

const { refresh: doVerify } = useApi({
  model: verifyModel,
  method: 'POST',
  url: 'login/verify_totp',
})

async function submit() {
  if (code.value.length !== 6 || loading.value) return
  try {
    loading.value = true
    await doVerify({
      data: {
        username: props.username,
        password: props.password,
        service: props.service,
        code: code.value,
      },
    })
    const token = verifyModel.value?.access_token
    if (!token) {
      openModal({ type: 'error', message: t('tokenFailed') })
      return
    }
    emit('success', { access_token: token })
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : t('invalidCode')
    openModal({ type: 'error', message: msg })
    code.value = ''
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="totp-challenge">
    <p class="text-muted mb-3">{{ t('instruction') }}</p>
    <form @submit.prevent="submit">
      <div class="mb-3">
        <OtpCodeInput v-model="code" :disabled="loading" @complete="submit" />
      </div>
      <div class="d-flex gap-2 justify-content-end">
        <button type="button" class="btn btn-outline-secondary" :disabled="loading" @click="emit('close')">
          {{ t('cancel') }}
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="code.length !== 6 || loading"
        >
          {{ loading ? t('verifying') : t('signIn') }}
        </button>
      </div>
    </form>
  </div>
</template>

<i18n>
{
  "ru": {
    "tokenFailed": "Не удалось получить токен входа",
    "invalidCode": "Неверный код",
    "instruction": "Введите 6-значный код из приложения-аутентификатора.",
    "cancel": "Отмена",
    "verifying": "Проверка…",
    "signIn": "Войти"
  },
  "en": {
    "tokenFailed": "Unable to obtain sign-in token",
    "invalidCode": "Invalid code",
    "instruction": "Enter the 6-digit code from your authenticator app.",
    "cancel": "Cancel",
    "verifying": "Verifying…",
    "signIn": "Sign in"
  }
}
</i18n>
